import React from "react";
import { Table, Badge, Form, Spinner } from "react-bootstrap";
import { ExclamationCircle } from "react-bootstrap-icons";
import { Currency } from "../../../lib/components/Currency";
import { FormattedDate } from "../../../lib/components/FormattedDate";
import { CheckBox } from "./CheckBox";
import { Actions } from "./Actions";
import { UNASSIGNED, CommissionStatus } from "./constants";
import { Link } from "react-router-dom";
import { CollapsibleAgentPanel } from "../CollapsibleAgentPanel";

function hasPayoutRun(value) {
  if (value == null) return false;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return Boolean(value);
}

const isIncludedInPayoutRun = (contract) => {
  return hasPayoutRun(contract?.payoutRunId);
};

const getPayoutRunName = (contract) => {
  return contract?.payoutRunName ?? null;
};

export function AgentGroupCard({
  groupKey,
  rows,
  selected,
  onToggleOne,
  onToggleGroup,
  isCollapsed,
  onToggleCollapse,
  busy,
  errored,
  onUpdateStatus,
  onUpdateDisposition,
  savingDispositions,
}) {
  const unassigned = groupKey === UNASSIGNED;
  const pend = rows.filter(
    (c) => c.commissionStatus === CommissionStatus.Pending,
  );
  const pendIds = pend.map((c) => c.number);
  const pendAmount = pend.reduce((s, c) => s + c.netPrice, 0);
  const groupSelCount = pendIds.filter((id) => selected.has(id)).length;
  const allSel = pendIds.length > 0 && groupSelCount === pendIds.length;
  const someSel = groupSelCount > 0 && !allSel;
  const label = unassigned ? "Unassigned" : groupKey;

  return (
    <CollapsibleAgentPanel
      title={label}
      subtitle={
        unassigned ? (
          <span className="d-flex align-items-center gap-1 text-warning-emphasis">
            <ExclamationCircle size={14} />
            No agent, these can only be declined
          </span>
        ) : null
      }
      badge={
        groupSelCount > 0 ? (
          <Badge
            bg="success-subtle"
            text="success-emphasis"
            className="flex-shrink-0"
          >
            {groupSelCount} selected
          </Badge>
        ) : null
      }
      summary={
        <>
          <p className="fw-semibold tnum mb-0">
            <Currency amount={pendAmount} />
          </p>
          <p
            className="text-uppercase text-secondary mb-0"
            style={{ fontSize: 11, letterSpacing: "0.04em" }}
          >
            pending
          </p>
        </>
      }
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      borderVariant={unassigned ? "warning" : "success"}
      backgroundVariant={unassigned ? "warning-bg-subtle" : "tertiary-bg"}
      className="shadow-sm overflow-hidden"
    >
      <Table
        hover
        responsive
        className="mb-0 align-middle"
        style={{ tableLayout: "fixed", minWidth: 720 }}
      >
        <colgroup>
          <col style={{ width: 40 }} />
          <col style={{ width: 100 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 100 }} />
          <col />
          <col />
          <col style={{ width: 120 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <thead>
          <tr
            className="small text-uppercase text-secondary"
            style={{ fontSize: 11, letterSpacing: "0.04em" }}
          >
            <th className="text-center fw-medium">
              {pendIds.length > 0 && (
                <CheckBox
                  checked={allSel}
                  indeterminate={someSel}
                  onChange={() => onToggleGroup(pendIds, !allSel)}
                  ariaLabel={`Select all pending for ${label}`}
                />
              )}
            </th>
            <th className="fw-medium">Signed</th>
            <th className="fw-medium">Provider #</th>
            <th className="fw-medium">Contract #</th>
            <th className="fw-medium">Status</th>
            <th className="fw-medium">Recipient(s)</th>
            <th className="fw-medium">Purchaser</th>
            <th className="fw-medium">Disposition</th>
            <th className="fw-medium text-end">Amount Paid</th>
            <th className="fw-medium text-end">Plan Total</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const isPending = c.commissionStatus === CommissionStatus.Pending;
            const isContractPending =
              String(c.status ?? "")
                .trim()
                .toLowerCase() === "pending";
            const isSel = selected.has(c.number);
            const canReset = !isIncludedInPayoutRun(c);
            const payoutRunName = getPayoutRunName(c);
            const statusLabel = c.status ?? "-";
            const recipients = c.recipients ?? [];
            const purchaserName = c.purchaser
              ? `${c.purchaser.firstName} ${c.purchaser.lastName}`
              : "-";
            const amountPaid = c.paid ?? c.amountPaid ?? 0;
            const resetBlockedReason = !canReset
              ? payoutRunName
                ? `Part of payout run "${payoutRunName}" and cannot be reset.`
                : "Part of a payout run and cannot be reset."
              : null;
            const rowClassName = [
              isSel ? "row-selected" : null,
              isContractPending ? "row-pending-contract" : null,
            ]
              .filter(Boolean)
              .join(" ");

            const lookupValues = c.provider?.fieldLookupValues;
            const hasLookupValues =
              Array.isArray(lookupValues) && lookupValues.length > 0;
            const sortedLookupValues = hasLookupValues
              ? [...lookupValues].sort(
                  (a, b) => (a.lookupOrder ?? 0) - (b.lookupOrder ?? 0),
                )
              : [];
                        const isSavingDisposition = savingDispositions.has(c.number);

            return (
              <tr key={c.number} className={rowClassName || undefined}>
                <td className="text-center">
                  {isPending && (
                    <CheckBox
                      checked={isSel}
                      onChange={() => onToggleOne(c.number)}
                      ariaLabel={`Select contract ${c.number}`}
                    />
                  )}
                </td>
                <td className="tnum">
                  <FormattedDate date={c.signed} />
                </td>
                <td className="tnum">
                  <Link to={`/contracts/${c.number}`} target="_blank">
                    {c.providerNumber}
                  </Link>
                </td>
                <td className="tnum">
                  <Link to={`/contracts/${c.number}`} target="_blank">
                    {c.number}
                  </Link>
                </td>
                <td>{statusLabel}</td>
                <td
                  className="text-truncate"
                  title={
                    recipients
                      .map((r) => `${r.firstName} ${r.lastName}`)
                      .join(", ") || "-"
                  }
                >
                  {recipients.map((r, ri) => (
                    <div key={ri}>
                      {r.firstName} {r.lastName}
                      <br />
                    </div>
                  ))}
                  {recipients.length === 0 && "-"}
                </td>
                <td
                  className="text-truncate"
                  title={purchaserName}
                >
                  {purchaserName}
                </td>
                {hasLookupValues && isPending ? (
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Select
                        size="sm"
                        value={c.disposition || ""}
                        onChange={(e) =>
                          onUpdateDisposition(c.number, e.target.value)
                        }
                        aria-label={`Select disposition for contract ${c.number}`}
                        disabled={isSavingDisposition}
                      >
                        <option value="">--Select--</option>
                        {sortedLookupValues.map((v, i) => (
                          <option
                            key={v.lookupValue ?? i}
                            value={v.lookupValue}
                          >
                            {v.lookupDescription}
                          </option>
                        ))}
                      </Form.Select>
                      {isSavingDisposition && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="flex-shrink-0 text-secondary"
                          role="status"
                        >
                          <span className="visually-hidden">
                            Saving disposition
                          </span>
                        </Spinner>
                      )}
                    </div>
                  </td>
                ) : (
                  <td className="text-truncate">{c.disposition}</td>
                )}
                <td className="tnum text-end">
                  <Currency amount={amountPaid} />
                </td>
                <td className="tnum text-end">
                  <Currency amount={c.netPrice} />
                </td>
                <td className="text-end">
                  <Actions
                    contract={c}
                    unassigned={unassigned}
                    busy={busy.has(c.number)}
                    errored={errored.has(c.number)}
                    canReset={canReset}
                    resetBlockedReason={resetBlockedReason}
                    onApprove={() =>
                      onUpdateStatus([c.number], CommissionStatus.Approved)
                    }
                    isApproveDisabled={isContractPending}
                    approveDisabledReason="Contract cannot be approved because it is still pending."
                    isDeclineDisabled={isContractPending}
                    declineDisabledReason="Contract cannot be declined because it is still pending."
                    onDecline={() =>
                      onUpdateStatus([c.number], CommissionStatus.Declined)
                    }
                    onReset={
                      canReset
                        ? () =>
                            onUpdateStatus([c.number], CommissionStatus.Pending)
                        : undefined
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </CollapsibleAgentPanel>
  );
}
